import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Form, Input, Modal, Select, Switch, Typography } from 'antd';
import type { AdminProduct, ChaveDeAcesso } from '@titularis/api-client';
import { api } from '../../api';
import { useOpcoesDeChave } from '../../opcoesDeChave';

/**
 * O formulário do produto, para criar e para editar.
 *
 * Um componente só porque os campos são os mesmos, com **uma** diferença que
 * vale a condição: a chave só aparece na criação. Ela é a identidade do produto
 * fora do banco - viaja no corpo do checkout, no seed e nos links já
 * compartilhados -, e renomeá-la quebraria o que aponta para ela sem quebrar
 * nada visível aqui. A API também a recusa: a tela não deve oferecer o que a
 * rota vai negar.
 */
interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente é criação; presente, edição. */
  produto?: AdminProduct;
  aoCriar?: (produto: AdminProduct) => void;
}

interface Campos {
  key: string;
  name: string;
  description?: string | null;
  grants: ChaveDeAcesso[];
  active: boolean;
}

export function FormularioDeProduto({ aberto, aoFechar, produto, aoCriar }: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Campos>();
  const { grupos, carregando: carregandoChaves } = useOpcoesDeChave();

  const editando = Boolean(produto);

  /*
   * O formulário é remontado a cada abertura (`destroyOnHidden`), mas o
   * `initialValues` só vale na primeira: abrir para editar outro produto sem
   * este efeito mostraria os dados do anterior.
   */
  useEffect(() => {
    if (!aberto) return;
    form.setFieldsValue({
      key: produto?.key ?? '',
      name: produto?.name ?? '',
      description: produto?.description ?? '',
      grants: (produto?.grants ?? []) as ChaveDeAcesso[],
      active: produto?.active ?? true,
    });
  }, [aberto, produto, form]);

  const salvar = useMutation({
    mutationFn: (campos: Campos) =>
      produto
        ? api.updateProduct(produto.id, {
            name: campos.name,
            description: campos.description?.trim() ? campos.description : null,
            grants: campos.grants,
            active: campos.active,
          })
        : api.createProduct({
            key: campos.key.trim(),
            name: campos.name,
            description: campos.description?.trim() ? campos.description : null,
            grants: campos.grants,
            active: campos.active,
          }),
    onSuccess: async (salvo) => {
      message.success(editando ? 'Produto atualizado.' : 'Produto criado.');
      await queryClient.invalidateQueries({ queryKey: ['produtos'] });
      if (!editando) aoCriar?.(salvo);
      else aoFechar();
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  return (
    <Modal
      open={aberto}
      title={editando ? 'Editar produto' : 'Novo produto'}
      okText="Salvar"
      cancelText="Cancelar"
      confirmLoading={salvar.isPending}
      onCancel={aoFechar}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(campos) => salvar.mutate(campos)}
        style={{ marginTop: 16 }}
      >
        {!editando && (
          <Form.Item
            name="key"
            label="Chave"
            rules={[
              { required: true, message: 'A chave identifica o produto fora do banco.' },
              {
                pattern: /^[a-z][a-z0-9_]{1,39}$/,
                message: 'Só minúsculas, números e "_", começando por letra.',
              },
            ]}
            extra="Não muda depois: ela viaja no checkout e nos links já compartilhados."
          >
            <Input placeholder="enac" autoComplete="off" />
          </Form.Item>
        )}

        <Form.Item
          name="name"
          label="Nome"
          rules={[{ required: true, message: 'É o nome que o comprador vê.' }]}
        >
          <Input placeholder="Enac" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Descrição"
          extra="Aparece na página de planos e vai junto na cobrança do provedor."
        >
          <Input.TextArea rows={3} maxLength={2000} showCount />
        </Form.Item>

        <Form.Item
          name="grants"
          label="O que a compra libera"
          rules={[{ required: true, message: 'Produto sem chave de acesso não libera nada.' }]}
          extra={
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Uma compra pode dar mais de um direito: o acesso do produto, e as Legislações
              Comentadas que entram junto.
            </Typography.Text>
          }
        >
          {/*
            `optionFilterProp="label"` porque o padrão do Antd filtra pelo
            `value`: sem isto, digitar "2ª fase" na caixa devolve "Não há
            dados", já que o valor da opção é `essay`. Medido no navegador - e
            com as leis a busca virou o único jeito de achar o que se quer.
          */}
          <Select
            mode="multiple"
            optionFilterProp="label"
            loading={carregandoChaves}
            placeholder="Escolha o que esta compra concede"
            options={grupos}
          />
        </Form.Item>

        {/*
          **O `grants` vale para as próximas compras** (D-47). Acrescentar uma lei
          aqui não alcança quem já comprou, e essa regra precisa ser escolhida em
          vez de descoberta pelo suporte três semanas depois.
        */}
        {editando && (produto?.vendas.pagos ?? 0) > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
            message="Mudar o que a compra libera vale daqui para frente"
            description={`Os ${produto!.vendas.pagos} que já compraram continuam com o que foi concedido no dia. Para dar a chave nova a eles, é uma concessão manual por pessoa, em Assinantes.`}
          />
        )}

        <Form.Item
          name="active"
          label="À venda"
          valuePropName="checked"
          extra="Desligado, some da vitrine. O histórico de vendas continua de pé."
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
